import { createApi } from '@reduxjs/toolkit/query/react'
import type {
	AdminAddressApiResponse,
	BalanceHistoryApiResponse,
	BalanceHistoryRequest,
	CreateNowPaymentApiResponse,
	CreateNowPaymentRequest,
	CurrencyDetailApiResponse,
	CurrencyListApiResponse,
	DisableBalanceApiResponse,
	DisableBalanceRequest,
	EnableBalancesApiResponse,
	EnableBalancesRequest,
	MetaMaskDepositApiResponse,
	MetaMaskDepositRequest,
	PaymentBalancesApiResponse,
	PhantomDepositApiResponse,
	PhantomDepositRequest,
	SetDefaultBalanceApiResponse,
	SetDefaultBalanceRequest,
} from '@/types/payment'
import { playerBaseQuery } from './playerBaseQuery'

export const paymentApi = createApi({
	reducerPath: 'paymentApi',
	baseQuery: playerBaseQuery,
	tagTypes: ['Balances', 'Currencies'],
	endpoints: (builder) => ({
		getCurrencies: builder.query<CurrencyListApiResponse, void>({
			query: () => '/payment/currency',
			providesTags: ['Currencies'],
		}),
		getCurrency: builder.query<CurrencyDetailApiResponse, string>({
			query: (id) => `/payment/currency/${id}`,
			providesTags: (_result, _error, id) => [{ type: 'Currencies', id }],
		}),
		getBalances: builder.query<PaymentBalancesApiResponse, void>({
			query: () => '/payment/balances',
			providesTags: ['Balances'],
		}),
		getBalanceHistory: builder.query<BalanceHistoryApiResponse, BalanceHistoryRequest>({
			query: ({ page, per_page }) => ({
				url: '/history/balance',
				params: { page, per_page },
			}),
		}),
		enableBalances: builder.mutation<EnableBalancesApiResponse, EnableBalancesRequest>({
			query: (body) => ({
				url: '/payment/balance',
				method: 'POST',
				body,
			}),
			invalidatesTags: ['Balances'],
		}),
		disableBalance: builder.mutation<DisableBalanceApiResponse, DisableBalanceRequest>({
			query: (body) => ({
				url: '/payment/balance',
				method: 'DELETE',
				body,
			}),
			invalidatesTags: ['Balances'],
		}),
		setDefaultBalance: builder.mutation<SetDefaultBalanceApiResponse, SetDefaultBalanceRequest>({
			query: (body) => ({
				url: '/payment/default-balance',
				method: 'POST',
				body,
			}),
			invalidatesTags: ['Balances'],
		}),
		createNowPayment: builder.mutation<CreateNowPaymentApiResponse, CreateNowPaymentRequest>({
			query: (body) => ({
				url: '/payment/create-now-payment',
				method: 'POST',
				body,
			}),
		}),
		getAdminAddress: builder.query<AdminAddressApiResponse, void>({
			query: () => '/payment/admin-address',
		}),
		depositMetaMask: builder.mutation<MetaMaskDepositApiResponse, MetaMaskDepositRequest>({
			query: (body) => ({
				url: '/payment/deposit-metamask',
				method: 'POST',
				body,
			}),
			invalidatesTags: ['Balances'],
		}),
		depositPhantom: builder.mutation<PhantomDepositApiResponse, PhantomDepositRequest>({
			query: (body) => ({
				url: '/payment/deposit-phantom',
				method: 'POST',
				body,
			}),
			invalidatesTags: ['Balances'],
		}),
	}),
})

export const {
	useGetCurrenciesQuery,
	useGetCurrencyQuery,
	useLazyGetCurrencyQuery,
	useGetBalancesQuery,
	useGetBalanceHistoryQuery,
	useEnableBalancesMutation,
	useDisableBalanceMutation,
	useSetDefaultBalanceMutation,
	useCreateNowPaymentMutation,
	useGetAdminAddressQuery,
	useLazyGetAdminAddressQuery,
	useDepositMetaMaskMutation,
	useDepositPhantomMutation,
} = paymentApi
